import {
  DecisionPageLayout,
  DecisionSection,
} from "@/components/DecisionPageLayout";
import {
  SpravatoSafetyBlock,
  SpravatoInternalLinks,
} from "@/components/SpravatoSafetyBlock";
const SpravatoInsuranceDenied = () => (
  <DecisionPageLayout
    seo={{
      title:
        "When Insurance Has Already Told You No — SPRAVATO® | Neuro Wellness TMS Centers of America",
      description:
        "You've been through this before. Learn why SPRAVATO® coverage is different — FDA-approved, Medicare-covered, and we handle prior authorization and appeals for you.",
      canonical: "/spravato-insurance-denied",
      keywords:
        "SPRAVATO insurance denied, prior authorization, Medicare SPRAVATO coverage, treatment-resistant depression insurance, Coral Springs FL",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        name: "When Insurance Has Already Told You No — SPRAVATO®",
        about:
          "Understanding SPRAVATO® insurance coverage, prior authorization, and how the practice handles appeals.",
        provider: {
          "@id": "https://neurowellnesstms.com/#organization",
        },
      },
    }}
    badge="SPRAVATO® · Recognition"
    title="You've been through this before"
    subtitle="At some point you concluded that mental health coverage is theoretical, and you stopped asking."
    ctaHeading="Find out where you actually stand"
    ctaText="The benefits check costs nothing and commits you to nothing. Schedule a free benefits check or call (954) 827-9793."
  >
    <DecisionSection heading="You've been through this before">
      <p>
        Maybe it was therapy sessions capped at a number that ran out by March.
        Maybe a treatment your doctor recommended that came back denied. Maybe a
        claim you fought for months and lost.
      </p>
      <p>
        At some point you concluded that mental health coverage is theoretical,
        and you stopped asking.
      </p>
      <p>
        That's a reasonable conclusion from the evidence. It's also, for this
        particular treatment, not necessarily true.
      </p>
    </DecisionSection>

    <DecisionSection heading="Why SPRAVATO® is different from what got denied">
      <p>
        The distinction is FDA approval. SPRAVATO® is approved for
        treatment-resistant depression, which means insurers have an established
        process for covering it — rather than the ambiguity around treatments
        used off-label.
      </p>
      <p>
        Medicare Part B covers it. Most commercial plans cover it. Both require
        prior authorization.
      </p>
    </DecisionSection>

    <DecisionSection heading="What prior authorization actually involves">
      <p>
        Your plan wants documentation that you meet criteria for
        treatment-resistant depression. Typically:
      </p>
      <ul className="space-y-2">
        <li className="flex gap-3 text-foreground/85 text-base leading-relaxed">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
          <span>
            Records showing at least two antidepressant trials, usually from
            different classes, without adequate response
          </span>
        </li>
        <li className="flex gap-3 text-foreground/85 text-base leading-relaxed">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
          <span>Sometimes a current depression severity score</span>
        </li>
        <li className="flex gap-3 text-foreground/85 text-base leading-relaxed">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
          <span>Sometimes therapy history</span>
        </li>
      </ul>
      <p>It generally takes somewhere between a few days and two weeks.</p>
      <p>
        We do this part. Our team gathers records, submits the request, and
        files the appeal if it comes back denied. Most denials come from
        incomplete documentation rather than genuine ineligibility, and appeals
        with fuller records often succeed.
      </p>
      <p>
        You've probably fought an insurer alone before. You wouldn't be doing
        that here.
      </p>
    </DecisionSection>

    <DecisionSection heading="Two things nobody tells you about the cost">
      <p>
        There are two separate charges — the medication and the required
        observation visit. Programs that reduce one don't necessarily touch the
        other, which is how people end up with unexpected bills.
      </p>
      <p>
        There's a manufacturer savings program for eligible commercially insured
        patients that can substantially reduce the medication cost, plus a
        separate program that can offset observation costs. Neither applies to
        Medicare or Medicaid. There's also an assistance foundation for
        uninsured patients.
      </p>
      <p>
        We'll enroll you in whatever you qualify for and give you a real number
        in writing before your first session.
      </p>
    </DecisionSection>

    <SpravatoInternalLinks />
    <SpravatoSafetyBlock />
  </DecisionPageLayout>
);
export default SpravatoInsuranceDenied;
